import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ProjectsFacade } from '@piqet-angular/data';
import {
  AccessPermissionService,
  CustomModalService,
  defaultColumn,
  DELETE_RECORD_LABEL,
  grid_widths_map,
  mainModuleName,
  rolesModuleName,
} from '@piqet-angular/shared';
import { right } from '@popperjs/core';
import { ColDef } from 'ag-grid-community';
import { ProjectsService } from 'libs/data/src/lib/services/projects/projects.service';
import { SwalService } from 'libs/shared/src/lib/service/swal.service';
import { NumericCellEditor } from 'libs/ui/src/lib/numeri-cell-editor/numeric-cell-editor.component';
import { cloneDeep } from 'lodash';
import { ToastrService } from 'ngx-toastr';
import { Subject, from } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class ProjectScenarioConfigDataGridService {
  packageScenarioId: any;
  inlineEditing = new Subject<any>();
  inlineEditingObservable$ = this.inlineEditing.asObservable();

  constructor(
    private modal: CustomModalService,
    private accessService: AccessPermissionService,
    private route: ActivatedRoute,
    private projectFacade: ProjectsFacade,
    private toastr: ToastrService,
    private swalService: SwalService
  ) {}

  deletePallet = new Subject<[any, any]>();
  deletePalletData$ = this.deletePallet.asObservable();

  deleteOrgYear = new Subject<[any]>();
  deleteOrgYearData$ = this.deleteOrgYear.asObservable();

  deletePackageContentConfig = new Subject<[any]>();
  deletePackageContentConfigData$ = this.deletePackageContentConfig.asObservable();

  editPackageContentConfig = new Subject<[any, any]>();
  editPackageContentConfigData$ = this.editPackageContentConfig.asObservable();

  editPackageContentSuccess = new Subject<any>();
  editPackageContentData$ = this.editPackageContentSuccess.asObservable();

  projectScenarioConfigurationData(headerName?: any): ColDef[] {
    let enableEditing: any;
    let resetData: any;
    const colDef: ColDef[] = [];
    colDef.push({
      ...defaultColumn,
      colId: 'StorageProcessname',
      headerName: 'Image',
      field: 'Image',
      cellRenderer: 'GridMyProjectImageComponent',
      cellClass: 'my-permission-cursor-pointer',
      minWidth: grid_widths_map.sm_column_xm,
      maxWidth: grid_widths_map.sm_column_xm,
      sortable: false,
      wrapText: true,
      autoHeight: true,
      lockVisible: false,
      resizable: false,
      cellRendererParams: {
        isPackagingConfig: 'pallet config',
      },
      wrapHeaderText: true,
    });

    colDef.push({
      ...defaultColumn,
      colId: 'StorageDays',
      headerName: 'Packaging level',
      field: 'name',
      wrapText: true,
      autoHeight: true,
      lockVisible: true,
      resizable: false,
      cellClass: (params: any) => {
        const classes = ['outerGridEdit', 'outerGridProjectEdit', 'elipsisFields'];
        if (!params.data.isLastChild) {
          classes.push('pointer-cursor');
        }
        return classes.join(' ');
      },
      cellEditorSelector: (params: any) => {
        if (params.colDef.colId === 'StorageDays') {
          return {
            component: NumericCellEditor,
          };
        }
        return undefined;
      },
      cellEditorParams: {
        from: 'project-pages-inline',
        typeColumn: 'text',
      },
      editable: true,
      cellRendererParams: {
        isPackagingConfig: 'pallet config',
      },
      cellRenderer: (params: any) => {
        // return `<span class="editable-icon editable-icon-padding" title="${params.value}"> ${params.value}  ${!params.data.isLastChild ? '<i class="fa fa-pencil icon-opacity" aria-hidden="true"></i>' : ''}</span>`;
        return `<span class="editable-icon editable-icon-padding-conversion"> ${params.value}  ${
          !params.data.isLastChild
            ? '<i class="fa fa-pencil icon-opacity" aria-hidden="true"></i>'
            : ''
        }</span>`;
      },
      valueSetter: (params: any) => {
        const newVal = params.newValue;
        const valueChanged = !resetData ? params.data.amount !== newVal : false;
        const oldValue = params.data[params.colDef.field];
        params = cloneDeep(params);
        if (valueChanged && newVal) {
          params.data[params.colDef.field] =
            typeof params.oldValue == 'number' ? parseFloat(newVal) : newVal;
          const inlineObject = {
            packagelevelinfoId: params.data.packageLevelInfoId,
            amount: params.data.amount,
            name: params.data.name,
          };
          if (!this.isEmpty(newVal) && valueChanged) {
            this.inlineEditing.next(inlineObject);
            return valueChanged;
          } else if (!parseInt(newVal)) {
            this.toastr.error('Quantity in packaging level is required');
            return oldValue;
          }
        }
      },
    });

    colDef.push({
      ...defaultColumn,
      colId: 'amount',
      headerName: 'Quantity in packaging level',
      field: 'amount',
      wrapText: true,
      autoHeight: true,
      lockVisible: false,
      resizable: false,
      sortable: false,
      editable: (params: any) => {
        return !params.data.isLastChild; // Make it editable if isLastChild is true, otherwise false
      },
      cellRenderer: (params: any) => {
        return `<span class="editable-icon editable-icon-padding-conversion"> ${params.value}  ${
          !params.data.isLastChild
            ? '<i class="fa fa-pencil icon-opacity" aria-hidden="true"></i>'
            : ''
        }</span>`;
      },
      cellEditorSelector: (params: any) => {
        if (params.colDef.colId === 'amount') {
          return {
            component: NumericCellEditor,
          };
        }
        return undefined;
      },
      cellEditorParams: {
        from: 'project-pages-inline',
      },
      cellClass: (params: any) => {
        const classes = ['outerGridEdit', 'outerGridProjectEdit', 'elipsisFields'];
        if (!params.data.isLastChild) {
          classes.push('pointer-cursor');
        }
        return classes.join(' ');
      },
      valueSetter: (params: any) => {
        const newVal = params.newValue;
        const valueChanged = !resetData ? params.data.amount !== newVal : false;
        const oldValue = params.data[params.colDef.field];
        params = cloneDeep(params);
        if (valueChanged && newVal) {
          params.data[params.colDef.field] =
            typeof params.oldValue == 'number' ? parseFloat(newVal) : newVal;
          const inlineObject = {
            packagelevelinfoId: params.data.packageLevelInfoId,
            amount: params.data.amount,
            name: params.data.name,
          };
          if (!this.isEmpty(newVal) && valueChanged && parseInt(newVal)) {
            this.inlineEditing.next(inlineObject);
            return valueChanged;
          } else if (!parseInt(newVal)) {
            this.toastr.error('Quantity in packaging level is required');
            return oldValue;
          }
        }
      },
      cellRendererParams: {
        suppressDragLeaveHidesColumns: true,
        isPackagingConfig: 'pallet config',
      },
      wrapHeaderText: true,
    });

    colDef.push({
      ...defaultColumn,
      colId: 'ProductLossName',
      headerName: 'Packaging level detail',
      cellClass: 'my-permission-cursor-pointer',
      field: 'packageLevelDetails',
      wrapText: true,
      autoHeight: true,
      lockVisible: false,
      sortable: false,
      resizable: false,
      cellRendererParams: {
        suppressDragLeaveHidesColumns: true,
        isPackagingConfig: 'pallet config',
      },
      wrapHeaderText: true,
    });

    colDef.push({
      ...defaultColumn,
      colId: 'ProductLossName',
      headerName: '',
      field: 'totalunitsperPallet',
      cellClass: 'my-permission-cursor-pointer',
      wrapText: true,
      autoHeight: true,
      lockVisible: false,
      resizable: false,
      sortable: false,
      cellRendererParams: {
        suppressDragLeaveHidesColumns: true,
        isPackagingConfig: 'pallet config',
      },
      wrapHeaderText: true,
    });

    this.isAccess('edit') &&
      colDef.push({
        ...defaultColumn,
        colId: 'Actions',
        headerName: 'Actions',
        field: 'Action',
        sortable: false,
        minWidth: grid_widths_map.sm_column_xxm,
        maxWidth: grid_widths_map.sm_column_xs,
        cellRenderer: 'inlineActionButtonComponent',
        cellRendererParams: (params: any) => ({
          showInlineEdit: params.data?.name?.toLowerCase() == 'pallet unit' ? true : false,
          showInlineDelete: true,
          from: 'pallet-config-check',
          copyData: (data: any) => false,
          inlineEditData: (data: any) => {
            enableEditing = data.data.enableEditing;
            resetData = data.data.resetData;
          },
          deleteInlineGrid: (data: any) => {
            this.swalService.confirm('Are you sure?', DELETE_RECORD_LABEL).then(confirmed => {
              if (confirmed) {
                this.deletePallet.next(data);
              }
            });
          },
        }),
        cellClass: 'mainGridEdit',
        lockVisible: true,
        lockPosition: right,
        resizable: false,
        pinned: right,
      });

    return colDef;
  }

  isAccess = (type: 'view' | 'edit' | 'create' | 'delete') => {
    return this.accessService.hasModulePermission(
      rolesModuleName.packageScenario,
      mainModuleName.projectsModule,
      type
    );
  };

  contentConfigurationData(headerName?: any): ColDef[] {
    const colDef: ColDef[] = [];

    colDef.push({
      ...defaultColumn,
      colId: 'ProductName',
      headerName: 'Contents',
      field: 'productName',
      // sort: 'desc',
      wrapText: true,
      autoHeight: true,
      lockVisible: false,
      resizable: false,
      editable: false,
      cellRendererParams: {
        from: 'packagingContentConfig',
      },
      wrapHeaderText: true,
    });

    colDef.push({
      ...defaultColumn,
      colId: 'MassOfProductInGram',
      headerName: 'Mass/volume in retail pack',
      field: 'massOfProductInGram',
      wrapText: true,
      autoHeight: true,
      lockVisible: false,
      resizable: false,
      editable: false,
      cellRendererParams: {
        from: 'packagingContentConfig',
      },
      cellEditorSelector: (params: any) => {
        if (params.colDef.colId === 'quantity') {
          return {
            component: NumericCellEditor,
          };
        }
        return undefined;
      },
      valueSetter: (params: any) => {
        const newValInt = params.newValue;
        const valueChanged = params.data.quantity !== newValInt;
        params = cloneDeep(params);
        if (valueChanged) {
          params.data.quantity =
            typeof params.oldValue == 'number' ? parseFloat(newValInt) : newValInt;
        }
        return valueChanged;
      },
      wrapHeaderText: true,
    });

    colDef.push({
      ...defaultColumn,
      colId: 'FunctionalUnitsPerProduct',
      headerName: 'Functional unit multiplier',
      field: 'functionalUnitsPerProduct',
      wrapText: true,
      autoHeight: true,
      lockVisible: false,
      resizable: false,
      editable: false,
      cellRendererParams: {
        from: 'packagingContentConfig',
      },
      wrapHeaderText: true,
    });

    colDef.push({
      ...defaultColumn,
      colId: 'Actions',
      headerName: 'Actions',
      field: 'Action',
      sortable: false,
      minWidth: grid_widths_map.sm_column_xs,
      maxWidth: grid_widths_map.sm_column_xs,
      cellClass: 'my-permission-cursor-pointer',
      cellRenderer: 'actionRenderer',
      cellRendererParams: {
        showEdit: true,
        showDelete: true,
        value: 'manageTeams',
        editData: (data: any) => {
          this.editPackageContentConfig.next(data);
        },
        deleteData: (data: any) => {
          this.deleteConfirmationPackaging(data);
        },
      },
      lockVisible: false,
      lockPosition: right,
      resizable: false,
    });

    return colDef;
  }

  deleteConfirmation(data: any) {
    this.swalService.confirm('Are you sure?', DELETE_RECORD_LABEL).then(confirmed => {
      if (confirmed && data.productGroupName != 'Select a product') {
        this.deleteOrgYear.next([data]);
      } else if (confirmed && data.productGroupName == 'Select a product') {
        this.projectFacade.deleteOrgYearActions(data);
      }
    });
  }

  deleteConfirmationPackaging(data: any) {
    this.swalService.confirm('Are you sure?', DELETE_RECORD_LABEL).then(confirmed => {
      if (confirmed) {
        this.deletePackageContentConfig.next([data]);
      }
    });
  }

  isEmpty(val: any) {
    if (val !== undefined || val !== null || val !== '') {
      val = typeof val == 'string' ? val?.trim() : val;
    }
    return val === undefined || val === null || val === '';
  }

  getScenarioDetailList() {
    this.editPackageContentSuccess.next(() => {});
  }
}
